import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from './notify';
import { ShieldCheck, Check, Ban, Trash2, Undo2, Pill, FlaskConical, ScanLine, BedDouble, ChevronDown, Send, Briefcase, UserCheck } from 'lucide-react';
import { VisitCard, ItemsTable, money, deptLabel } from './VisitBits';
import ConfirmDialog from '../../components/ConfirmDialog';

import PreauthCodesRegister from './PreauthCodesRegister';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Item list with per-row "remove" checkboxes used during approval.
const RemovableItems = ({ title, items, visit, idKey, selected, onToggle, showInstructions = false }) => {
  if (!items || items.length === 0) return null;
  return (
    <div className="mt-3">
      <p className="text-sm font-semibold text-gray-700 mb-1">{title}</p>
      <div className="border border-gray-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-center p-2 font-semibold text-gray-600 w-20">Remove</th>
              <th className="text-left p-2 font-semibold text-gray-600">Item</th>
              <th className="text-center p-2 font-semibold text-gray-600">Qty</th>
              <th className="text-right p-2 font-semibold text-gray-600">Total</th>
              {showInstructions && <th className="text-left p-2 font-semibold text-gray-600">Instructions</th>}
            </tr>
          </thead>
          <tbody>
            {items.map((i) => {
              const id = i[idKey];
              const checked = selected.includes(id);
              return (
                <tr key={id} className={`border-t border-gray-100 ${checked ? 'bg-red-50' : ''}`}>
                  <td className="p-2 text-center">
                    <Checkbox checked={checked} onCheckedChange={() => onToggle(id)} data-testid={`remove-item-${id}`} />
                  </td>
                  <td className={`p-2 ${checked ? 'line-through text-red-500' : 'text-gray-800'}`}>{i.item_name}</td>
                  <td className="p-2 text-center">{i.quantity || 1}</td>
                  <td className="p-2 text-right">{money(visit, i.cost * (i.quantity || 1))}</td>
                  {showInstructions && <td className="p-2 text-gray-600">{i.instructions || '—'}</td>}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default function ApprovalsPanel({ visits, refresh, authCfg, isSuperAdmin = true }) {
  const preauthQueue = visits.filter((v) => v.status === 'AWAITING_PREAUTH');
  const admissionQueue = visits.filter((v) => v.status === 'AWAITING_ADMISSION_APPROVAL');
  const labQueue = visits.filter((v) => v.status === 'AWAITING_LAB_APPROVAL');
  const radQueue = visits.filter((v) => v.status === 'AWAITING_RAD_APPROVAL');
  const serviceQueue = visits.filter((v) => v.status === 'AWAITING_SERVICE_APPROVAL');
  const internalReferralQueue = visits.filter((v) => v.status === 'AWAITING_REFERRAL_APPROVAL');
  const rxQueue = visits.filter((v) => v.status === 'AWAITING_RX_APPROVAL');

  const [labThreshold, setLabThreshold] = useState({ default_usd: 100, overrides: [] });
  const [radThreshold, setRadThreshold] = useState({ default_usd: 100, overrides: [] });
  const [serviceThreshold, setServiceThreshold] = useState({ default_usd: 100, overrides: [] });
  const [rxThreshold, setRxThreshold] = useState({ default_usd: 100, overrides: [] });
  const [hospitals, setHospitals] = useState([]);
  const [dialog, setDialog] = useState(null);
  const [filter, setFilter] = useState('all');
  const [preauths, setPreauths] = useState([]);
  const [referralQueue, setReferralQueue] = useState([]);

  const loadReferrals = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/visits/referrals/external?status=pending`, authCfg);
      setReferralQueue(res.data.visits || []);
    } catch (e) {
      // non-critical
    }
  }, [authCfg]);

  useEffect(() => { loadReferrals(); }, [loadReferrals, visits]);

  const decideReferral = (visit, decision) => {
    const ext = visit.external_referral || {};
    if (decision === 'approve') {
      setDialog({
        title: 'Approve external referral',
        description: `Approve the referral of ${visit.patient_name} to ${ext.specialist} at ${ext.facility}? The hospital can then print the referral letter.`,
        confirmLabel: 'Approve Referral',
        onConfirm: async () => {
          try {
            const res = await axios.post(`${API}/visits/${visit.visit_id}/referral/approve`, {}, authCfg);
            toast.success(`External referral ${res.data.referral_number} approved for ${visit.patient_name}`);
            refresh();
            loadReferrals();
          } catch (e) {
            toast.error(e.response?.data?.detail || 'Action failed');
          }
        },
      });
      return;
    }
    setDialog({
      title: 'Reject external referral',
      description: `Reject the referral of ${visit.patient_name} to ${ext.specialist} at ${ext.facility}? The visit continues normally without a referral letter.`,
      withReason: true,
      confirmLabel: 'Reject',
      destructive: true,
      onConfirm: async (reason) => { await act(`${API}/visits/${visit.visit_id}/referral/reject`, { reason: reason || '' }, 'External referral rejected'); loadReferrals(); },
    });
  };
  // per-visit sets of item ids selected for removal
  const [removals, setRemovals] = useState({});

  const loadPreauths = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/visits/preauth/approved`, authCfg);
      setPreauths(res.data.visits || []);
    } catch (e) {
      // non-critical
    }
  }, [authCfg]);

  useEffect(() => { loadPreauths(); }, [loadPreauths]);

  const loadSettings = useCallback(async () => {
    try {
      const [labRes, radRes, serviceRes, rxRes, hRes] = await Promise.all([
        axios.get(`${API}/workflow/lab-threshold`, authCfg),
        axios.get(`${API}/workflow/rad-threshold`, authCfg),
        axios.get(`${API}/workflow/service-threshold`, authCfg),
        axios.get(`${API}/workflow/rx-threshold`, authCfg),
        axios.get(`${API}/admin/hospitals`, authCfg),
      ]);
      setLabThreshold(labRes.data);
      setRadThreshold(radRes.data);
      setServiceThreshold(serviceRes.data);
      setRxThreshold(rxRes.data);
      setHospitals(hRes.data || []);
    } catch (e) {
      // settings are non-critical for rendering the queue
    }
  }, [authCfg]);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const toggleRemoval = (visitId, itemId) => {
    setRemovals((prev) => {
      const cur = prev[visitId] || [];
      const next = cur.includes(itemId) ? cur.filter((x) => x !== itemId) : [...cur, itemId];
      return { ...prev, [visitId]: next };
    });
  };

  const act = async (url, body, okMsg) => {
    try {
      await axios.post(url, body, authCfg);
      toast.success(okMsg);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    }
  };

  const approvePreauth = (visit) => {
    setDialog({
      title: 'Approve pre-authorization',
      description: `Issue a pre-authorization number for ${visit.patient_name} at ${visit.hospital_name}? The patient will proceed to the doctor.`,
      confirmLabel: 'Approve & Issue Number',
      onConfirm: async () => {
        try {
          const res = await axios.post(`${API}/visits/${visit.visit_id}/preauth/approve`, {}, authCfg);
          toast.success(`Pre-authorization ${res.data.preauth_number} issued for ${visit.patient_name}`);
          refresh();
          loadPreauths();
        } catch (e) {
          toast.error(e.response?.data?.detail || 'Action failed');
        }
      },
    });
  };

  const rejectPreauth = (visit) => {
    setDialog({
      title: 'Reject pre-authorization',
      description: `Reject treatment pre-authorization for ${visit.patient_name} at ${visit.hospital_name}? The visit will be cancelled.`,
      withReason: true,
      confirmLabel: 'Reject',
      destructive: true,
      onConfirm: (reason) => act(`${API}/visits/${visit.visit_id}/preauth/reject`, { reason: reason || '' }, 'Pre-authorization rejected — visit cancelled'),
    });
  };

  // Approve; if the approver ticked items for removal, ask for a single reason first.
  const approve = (visit, kind) => {
    const url = `${API}/visits/${visit.visit_id}/${kind}/approve`;
    const removed = removals[visit.visit_id] || [];
    const label = kind === 'lab' ? 'lab tests' : kind === 'rad' ? 'radiology scans' : kind === 'service' ? 'service' : 'prescription';
    const dest = kind === 'lab' ? 'laboratory' : kind === 'rad' ? 'radiology' : kind === 'service' ? 'next step' : 'pharmacy';
    if (removed.length === 0) {
      act(url, {}, `${kind === 'lab' ? 'Lab tests' : kind === 'rad' ? 'Radiology scans' : kind === 'service' ? 'Services' : 'Prescription'} approved for ${visit.patient_name}${kind === 'service' ? ' — visit continues' : ` — sent to ${dest}`}`);
      return;
    }
    setDialog({
      title: `Remove ${removed.length} item(s) & approve`,
      description: `You are removing ${removed.length} ${label} item(s) before approval. Give a reason — the doctor will see it in the visit form. The remaining items proceed to the ${dest}.`,
      withReason: true,
      confirmLabel: 'Remove & Approve',
      onConfirm: async (reason) => {
        if (!reason || !reason.trim()) {
          toast.error('A reason is required to remove items');
          return;
        }
        await act(url, { removed_ids: removed, removal_reason: reason.trim() }, `Approved for ${visit.patient_name} — ${removed.length} item(s) removed, rest sent to ${dest}`);
        setRemovals((prev) => ({ ...prev, [visit.visit_id]: [] }));
      },
    });
  };

  const rejectLab = (visit) => {
    setDialog({
      title: 'Reject lab tests',
      description: `Reject the ordered lab tests for ${visit.patient_name}? The visit will return to the doctor to finalize without test results.`,
      withReason: true,
      confirmLabel: 'Reject Tests',
      destructive: true,
      onConfirm: (reason) => act(`${API}/visits/${visit.visit_id}/lab/reject`, { reason: reason || '' }, 'Lab tests rejected — visit returned to the doctor'),
    });
  };

  const rejectRad = (visit) => {
    setDialog({
      title: 'Reject radiology scans',
      description: `Reject the ordered radiology scans for ${visit.patient_name}? The remaining workflow continues without imaging results.`,
      withReason: true,
      confirmLabel: 'Reject Scans',
      destructive: true,
      onConfirm: (reason) => act(`${API}/visits/${visit.visit_id}/rad/reject`, { reason: reason || '' }, 'Radiology scans rejected'),
    });
  };

  const rejectServices = (visit) => {
    setDialog({
      title: 'Reject services',
      description: `Remove all the services awaiting approval for ${visit.patient_name}? The visit continues without them (consultation fees are kept).`,
      withReason: true,
      confirmLabel: 'Reject Services',
      destructive: true,
      onConfirm: (reason) => act(`${API}/visits/${visit.visit_id}/service/reject`, { reason: reason || '' }, 'Services rejected — visit continues without them'),
    });
  };

  const decideInternalReferral = (visit, decision) => {
    const ref = visit.referral || {};
    if (decision === 'approve') {
      setDialog({
        title: 'Approve internal referral',
        description: `Let ${visit.patient_name} join the ${deptLabel(ref.department, visit)} queue at ${visit.hospital_name}? The specialist consultation fee stays on the bill.`,
        confirmLabel: 'Approve Referral',
        onConfirm: async () => {
          try {
            const res = await axios.post(`${API}/visits/${visit.visit_id}/referral-internal/approve`, {}, authCfg);
            toast.success(`Referral approved — ${visit.patient_name} sent to the ${res.data.next_department_label || 'specialist'} queue`);
            refresh();
          } catch (e) {
            toast.error(e.response?.data?.detail || 'Action failed');
          }
        },
      });
      return;
    }
    setDialog({
      title: 'Reject internal referral',
      description: `Decline the referral of ${visit.patient_name} to ${ref.specialist}? The visit goes back to the ${deptLabel(ref.from_department)} who can order tests, prescribe or send the patient to pharmacy/billing — the specialist fee is replaced by the general consultation fee.`,
      withReason: true,
      confirmLabel: 'Reject Referral',
      destructive: true,
      onConfirm: (reason) => act(`${API}/visits/${visit.visit_id}/referral-internal/reject`, { reason: reason || '' }, 'Referral rejected — visit returned to the referring doctor'),
    });
  };

  const rejectRx = (visit, action) => {
    setDialog({
      title: action === 'return' ? 'Reject prescription — return to doctor' : 'Reject prescription — drop & bill',
      description:
        action === 'return'
          ? `The prescription for ${visit.patient_name} will be rejected and the visit returned to the doctor to revise the medication. Your reason will be shown to the doctor.`
          : `The prescription for ${visit.patient_name} will be dropped and the visit sent straight to billing (medications excluded from the bill).`,
      withReason: true,
      confirmLabel: action === 'return' ? 'Reject & Return' : 'Reject & Drop',
      destructive: true,
      onConfirm: (reason) =>
        act(
          `${API}/visits/${visit.visit_id}/rx/reject`,
          { reason: reason || '', action },
          action === 'return' ? 'Prescription rejected — returned to the doctor to revise' : 'Prescription dropped — visit sent to billing'
        ),
    });
  };

  const ClinicalContext = ({ visit }) => (
    <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-900 space-y-1">
      {visit.consultation?.complaints && <p><strong>Patient complaints:</strong> {visit.consultation.complaints}</p>}
      {(visit.consultation?.provisional_diagnosis || visit.consultation?.diagnosis) && <p><strong>Provisional Dx:</strong> {visit.consultation.provisional_diagnosis || visit.consultation.diagnosis}</p>}
      {visit.consultation?.final_diagnosis && <p><strong>Final Dx:</strong> {visit.consultation.final_diagnosis}</p>}
      {visit.consultation?.notes && <p><strong>Physical Examination:</strong> {visit.consultation.notes}</p>}
    </div>
  );

  const groups = [
    { id: 'preauth', label: 'Pre-auth Codes', count: preauthQueue.length, icon: ShieldCheck, tone: 'amber' },
    { id: 'admission', label: 'Admissions', count: admissionQueue.length, icon: BedDouble, tone: 'rose' },
    { id: 'referral', label: 'Referral Letters', count: referralQueue.length, icon: Send, tone: 'violet' },
    { id: 'internal', label: 'Internal Referrals', count: internalReferralQueue.length, icon: UserCheck, tone: 'indigo' },
    { id: 'lab', label: 'Laboratory', count: labQueue.length, icon: FlaskConical, tone: 'amber' },
    { id: 'rad', label: 'Radiology', count: radQueue.length, icon: ScanLine, tone: 'sky' },
    { id: 'service', label: 'Services', count: serviceQueue.length, icon: Briefcase, tone: 'teal' },
    { id: 'rx', label: 'Prescriptions', count: rxQueue.length, icon: Pill, tone: 'orange' },
  ];
  const totalPending = groups.reduce((s, g) => s + g.count, 0);
  const show = (id) => filter === 'all' || filter === id;

  return (
    <div className="space-y-6" data-testid="approvals-panel">
      <ConfirmDialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)} {...(dialog || {})} />

      {/* Summary strip — click a chip to focus on one queue */}
      <div className="bg-white rounded-xl border border-gray-200 p-3" data-testid="approvals-summary">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <h2 className="text-base font-bold text-gray-800 flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-teal-600" /> Acacia Office Approvals <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${totalPending > 0 ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`} data-testid="approvals-total">{totalPending} pending</span></h2>
          <button onClick={() => setFilter('all')} className={`text-xs font-medium ${filter === 'all' ? 'text-gray-400' : 'text-teal-700 hover:underline'}`} data-testid="approvals-filter-all">Show all</button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
          {groups.map((g) => (
            <button key={g.id} onClick={() => setFilter(filter === g.id ? 'all' : g.id)} className={`flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-left transition-colors ${filter === g.id ? 'border-teal-500 bg-teal-50 ring-1 ring-teal-300' : g.count > 0 ? 'border-gray-300 bg-white hover:bg-gray-50' : 'border-gray-200 bg-gray-50 text-gray-400'}`} data-testid={`approvals-filter-${g.id}`} aria-pressed={filter === g.id}>
              <span className="flex items-center gap-1.5 text-xs font-semibold"><g.icon className="w-4 h-4" /> {g.label}</span>
              <span className={`text-sm font-bold ${g.count > 0 ? 'text-gray-900' : 'text-gray-400'}`} data-testid={`approvals-count-${g.id}`}>{g.count}</span>
            </button>
          ))}
        </div>
      </div>

      {totalPending === 0 && filter === 'all' && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 text-center text-emerald-800 font-medium" data-testid="approvals-all-clear">
          All caught up — no hospital requests are waiting for approval.
        </div>
      )}

      {/* 1. Patient access — before treatment starts */}
      {(show('preauth') || show('admission') || show('referral') || show('internal')) && (
      <Category title="1. Patient Access" subtitle="Requests that decide whether treatment, admission or an external referral can go ahead" tone="amber" testId="approvals-category-access">
        {show('preauth') && (
        <SubSection title="Pre-authorization Codes" count={preauthQueue.length} icon={ShieldCheck} tone="amber" testId="approvals-section-preauth">
        <PreauthCodesRegister authCfg={authCfg} canEdit={isSuperAdmin} setDialog={setDialog} />
        {preauthQueue.length === 0 ? null : (
          <div className="space-y-3">
            {preauthQueue.map((v) => (
              <VisitCard key={v.visit_id} visit={v} showHospital>
                <div className="flex flex-wrap gap-2 mt-3">
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => approvePreauth(v)} data-testid={`preauth-approve-${v.visit_id}`}>
                    <Check className="w-4 h-4 mr-1" /> Approve & Issue Number
                  </Button>
                  <Button size="sm" variant="outline" className="border-red-300 text-red-600 hover:bg-red-50" onClick={() => rejectPreauth(v)} data-testid={`preauth-reject-${v.visit_id}`}>
                    <Ban className="w-4 h-4 mr-1" /> Reject
                  </Button>
                </div>
              </VisitCard>
            ))}
          </div>
        )}
        </SubSection>
        )}
        {show('admission') && (
        <SubSection title="Admission Requests" count={admissionQueue.length} icon={BedDouble} tone="rose" testId="approvals-section-admission">
        {admissionQueue.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-4 text-center text-sm text-gray-500" data-testid="admission-empty">
            No admission requests waiting.
          </div>
        ) : (
          <div className="space-y-3">
            {admissionQueue.map((v) => (
              <VisitCard key={v.visit_id} visit={v} showHospital>
                <div className="mt-2 p-3 bg-rose-50 border border-rose-200 rounded-lg text-sm text-rose-900 space-y-1" data-testid={`admission-details-${v.visit_id}`}>
                  <p><strong>Reason for admission:</strong> {v.admission?.reason}</p>
                  {v.admission?.ward && <p><strong>Ward:</strong> {v.admission.ward}</p>}
                  {v.admission?.bed_item && <p><strong>Bed charge:</strong> {v.admission.bed_item.item_name} — {money(v, v.admission.bed_item.cost)} per day</p>}
                  <p className="text-xs text-rose-700">Requested by {v.admission?.requested_by} ({deptLabel(v.admission?.department)})</p>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => act(`${API}/visits/${v.visit_id}/admission/approve`, {}, `Admission approved for ${v.patient_name}`)} data-testid={`admission-approve-${v.visit_id}`}>
                    <Check className="w-4 h-4 mr-1" /> Approve Admission
                  </Button>
                  <Button size="sm" variant="outline" className="border-red-300 text-red-600 hover:bg-red-50" onClick={() => setDialog({ title: 'Reject admission', description: `${v.patient_name} will continue as an outpatient with the doctor.`, withReason: true, confirmLabel: 'Reject', destructive: true, onConfirm: (reason) => act(`${API}/visits/${v.visit_id}/admission/reject`, { reason: reason || '' }, 'Admission rejected — patient returned to the doctor') })} data-testid={`admission-reject-${v.visit_id}`}>
                    <Ban className="w-4 h-4 mr-1" /> Reject
                  </Button>
                </div>
              </VisitCard>
            ))}
          </div>
        )}
        </SubSection>
        )}
        {show('referral') && (
        <SubSection title="External Referral Letters" count={referralQueue.length} icon={Send} tone="violet" testId="approvals-section-referral">
        {referralQueue.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-4 text-center text-sm text-gray-500" data-testid="referral-empty">
            No external referrals waiting for approval.
          </div>
        ) : (
          <div className="space-y-3">
            {referralQueue.map((v) => (
              <VisitCard key={v.visit_id} visit={v} showHospital>
                <div className="mt-2 p-3 bg-violet-50 border border-violet-200 rounded-lg text-sm text-violet-900 space-y-1" data-testid={`referral-details-${v.visit_id}`}>
                  <p><strong>Referred to:</strong> {v.external_referral?.specialist} at <strong>{v.external_referral?.facility}</strong> <span className={`ml-1 px-1.5 py-0.5 rounded text-xs font-bold ${v.external_referral?.urgency === 'Emergency' ? 'bg-red-100 text-red-800' : v.external_referral?.urgency === 'Urgent' ? 'bg-amber-100 text-amber-900' : 'bg-white text-gray-700'}`}>{v.external_referral?.urgency}</span></p>
                  <p className="whitespace-pre-line"><strong>Reason:</strong> {v.external_referral?.reason}</p>
                  <p className="text-xs text-violet-700">Requested by {v.external_referral?.referred_by} ({deptLabel(v.external_referral?.from_department)})</p>
                </div>
                <ClinicalContext visit={v} />
                <div className="flex flex-wrap gap-2 mt-3">
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => decideReferral(v, 'approve')} data-testid={`referral-approve-${v.visit_id}`}>
                    <Check className="w-4 h-4 mr-1" /> Approve Referral Letter
                  </Button>
                  <Button size="sm" variant="outline" className="border-red-300 text-red-600 hover:bg-red-50" onClick={() => decideReferral(v, 'reject')} data-testid={`referral-reject-${v.visit_id}`}>
                    <Ban className="w-4 h-4 mr-1" /> Reject
                  </Button>
                </div>
              </VisitCard>
            ))}
          </div>
        )}
        </SubSection>
        )}
        {show('internal') && (
        <SubSection title="Internal Referrals — patient waits before joining the specialist's queue" count={internalReferralQueue.length} icon={UserCheck} tone="indigo" testId="approvals-section-internal">
        {internalReferralQueue.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-4 text-center text-sm text-gray-500" data-testid="internal-referral-empty">
            No internal referrals waiting for approval.
          </div>
        ) : (
          <div className="space-y-3">
            {internalReferralQueue.map((v) => (
              <VisitCard key={v.visit_id} visit={v} showHospital>
                <div className="mt-2 p-3 bg-indigo-50 border border-indigo-200 rounded-lg text-sm text-indigo-900 space-y-1" data-testid={`internal-referral-details-${v.visit_id}`}>
                  <p><strong>Referred to:</strong> {v.referral?.specialist} ({deptLabel(v.referral?.department, v)} queue)</p>
                  {v.referral?.note && <p className="whitespace-pre-line"><strong>Reason:</strong> {v.referral.note}</p>}
                  <p className="text-xs text-indigo-700">Referred by {v.consultation?.doctor_name || v.referral?.referred_by} ({deptLabel(v.referral?.from_department)}) — the general consultation fee has been replaced by the specialist fee</p>
                </div>
                <ItemsTable title="Services & fees on this visit" items={v.service_items || []} visit={v} />
                <div className="flex flex-wrap gap-2 mt-3">
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => decideInternalReferral(v, 'approve')} data-testid={`internal-referral-approve-${v.visit_id}`}>
                    <Check className="w-4 h-4 mr-1" /> Approve — send to {deptLabel(v.referral?.department, v)}
                  </Button>
                  <Button size="sm" variant="outline" className="border-red-300 text-red-600 hover:bg-red-50" onClick={() => decideInternalReferral(v, 'reject')} data-testid={`internal-referral-reject-${v.visit_id}`}>
                    <Ban className="w-4 h-4 mr-1" /> Reject — back to the referring doctor
                  </Button>
                </div>
              </VisitCard>
            ))}
          </div>
        )}
        </SubSection>
        )}
      </Category>
      )}

      {/* 2. Investigations over the hospital threshold */}
      {(show('lab') || show('rad') || show('service')) && (
      <Category title="2. Orders Over Threshold" subtitle="Tests and services whose cost exceeds the hospital's approval limit — tick items to remove before approving" tone="sky" testId="approvals-category-investigations">
        {show('lab') && (
        <SubSection title="Laboratory Orders" count={labQueue.length} icon={FlaskConical} tone="amber" testId="approvals-section-lab">
        {labQueue.length === 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-6 text-center text-gray-500" data-testid="approvals-empty">
            No lab orders awaiting approval.
          </div>
        )}
        <div className="space-y-3">
          {labQueue.map((v) => (
            <VisitCard key={v.visit_id} visit={v} showHospital>
              <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800" data-testid={`approval-info-${v.visit_id}`}>
                Lab total <strong>${v.lab_approval?.lab_total_usd} USD</strong> exceeds this hospital's threshold of <strong>${v.lab_approval?.threshold_usd} USD</strong>
              </div>
              <ClinicalContext visit={v} />
              <RemovableItems
                title="Ordered Lab Tests (tick to remove before approving)"
                items={(v.lab_orders || []).filter((o) => o.status === 'pending')}
                visit={v}
                idKey="order_id"
                selected={removals[v.visit_id] || []}
                onToggle={(id) => toggleRemoval(v.visit_id, id)}
              />
              <div className="flex gap-3 mt-3">
                <Button onClick={() => approve(v, 'lab')} className="bg-green-600 hover:bg-green-700" data-testid={`approve-lab-${v.visit_id}`}>
                  <Check className="w-4 h-4 mr-1" /> {(removals[v.visit_id] || []).length > 0 ? `Remove ${(removals[v.visit_id] || []).length} & Approve` : 'Approve'}
                </Button>
                <Button variant="outline" onClick={() => rejectLab(v)} className="border-red-300 text-red-600 hover:bg-red-50" data-testid={`reject-lab-${v.visit_id}`}>
                  <Ban className="w-4 h-4 mr-1" /> Reject All (back to doctor)
                </Button>
              </div>
            </VisitCard>
          ))}
        </div>
        </SubSection>
        )}
        {show('rad') && (
        <SubSection title="Radiology Orders" count={radQueue.length} icon={ScanLine} tone="sky" testId="approvals-section-rad">
        {radQueue.length === 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-6 text-center text-gray-500" data-testid="rad-approvals-empty">
            No radiology orders awaiting approval.
          </div>
        )}
        <div className="space-y-3">
          {radQueue.map((v) => (
            <VisitCard key={v.visit_id} visit={v} showHospital>
              <div className="mt-2 p-3 bg-sky-50 border border-sky-200 rounded-lg text-sm text-sky-800" data-testid={`rad-approval-info-${v.visit_id}`}>
                Radiology total <strong>${v.rad_approval?.rad_total_usd} USD</strong> exceeds this hospital's threshold of <strong>${v.rad_approval?.threshold_usd} USD</strong>
              </div>
              <ClinicalContext visit={v} />
              <RemovableItems
                title="Ordered Radiology Scans (tick to remove before approving)"
                items={(v.rad_orders || []).filter((o) => o.status === 'pending')}
                visit={v}
                idKey="order_id"
                selected={removals[v.visit_id] || []}
                onToggle={(id) => toggleRemoval(v.visit_id, id)}
              />
              <div className="flex gap-3 mt-3">
                <Button onClick={() => approve(v, 'rad')} className="bg-green-600 hover:bg-green-700" data-testid={`approve-rad-${v.visit_id}`}>
                  <Check className="w-4 h-4 mr-1" /> {(removals[v.visit_id] || []).length > 0 ? `Remove ${(removals[v.visit_id] || []).length} & Approve` : 'Approve'}
                </Button>
                <Button variant="outline" onClick={() => rejectRad(v)} className="border-red-300 text-red-600 hover:bg-red-50" data-testid={`reject-rad-${v.visit_id}`}>
                  <Ban className="w-4 h-4 mr-1" /> Reject All
                </Button>
              </div>
            </VisitCard>
          ))}
        </div>
        </SubSection>
        )}
        {show('service') && (
        <SubSection title="Services Over Threshold" count={serviceQueue.length} icon={Briefcase} tone="teal" testId="approvals-section-service">
        {serviceQueue.length === 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-6 text-center text-gray-500" data-testid="service-approvals-empty">
            No services awaiting approval.
          </div>
        )}
        <div className="space-y-3">
          {serviceQueue.map((v) => (
            <VisitCard key={v.visit_id} visit={v} showHospital>
              <div className="mt-2 p-3 bg-teal-50 border border-teal-200 rounded-lg text-sm text-teal-800" data-testid={`service-approval-info-${v.visit_id}`}>
                Services total <strong>${Number(v.service_approval?.service_total_usd || 0).toFixed(2)} USD</strong> exceeds this hospital's threshold of <strong>${Number(v.service_approval?.threshold_usd || 0).toFixed(2)} USD</strong> (consultation fees are not counted)
              </div>
              <RemovableItems
                title="Services added by the doctor (tick to remove before approving)"
                items={(v.service_items || []).filter((s) => (v.service_approval?.pending_ids || []).includes(s.service_id))}
                visit={v}
                idKey="service_id"
                selected={removals[v.visit_id] || []}
                onToggle={(id) => toggleRemoval(v.visit_id, id)}
              />
              <div className="flex gap-3 mt-3">
                <Button onClick={() => approve(v, 'service')} className="bg-green-600 hover:bg-green-700" data-testid={`approve-service-${v.visit_id}`}>
                  <Check className="w-4 h-4 mr-1" /> {(removals[v.visit_id] || []).length > 0 ? `Remove ${(removals[v.visit_id] || []).length} & Approve` : 'Approve'}
                </Button>
                <Button variant="outline" onClick={() => rejectServices(v)} className="border-red-300 text-red-600 hover:bg-red-50" data-testid={`reject-service-${v.visit_id}`}>
                  <Ban className="w-4 h-4 mr-1" /> Reject All
                </Button>
              </div>
            </VisitCard>
          ))}
        </div>
        </SubSection>
        )}
      </Category>
      )}

      {/* 3. Medications over threshold */}
      {show('rx') && (
      <Category title="3. Medications Over Threshold" subtitle="Prescriptions whose cost exceeds the hospital's limit — check they match the complaints and results" tone="orange" testId="approvals-category-medications">
        <SubSection title="Prescriptions" count={rxQueue.length} icon={Pill} tone="orange" testId="approvals-section-rx">
        {rxQueue.length === 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-6 text-center text-gray-500" data-testid="rx-approvals-empty">
            No prescriptions awaiting approval.
          </div>
        )}
        <div className="space-y-3">
          {rxQueue.map((v) => (
            <VisitCard key={v.visit_id} visit={v} showHospital>
              <div className="mt-2 p-3 bg-orange-50 border border-orange-200 rounded-lg text-sm text-orange-800" data-testid={`rx-approval-info-${v.visit_id}`}>
                Prescription total <strong>${v.rx_approval?.rx_total_usd} USD</strong> exceeds this hospital's threshold of <strong>${v.rx_approval?.threshold_usd} USD</strong>. Check that the medication matches the complaints and lab results below.
              </div>
              <ClinicalContext visit={v} />
              {(v.lab_orders || []).filter((o) => o.status === 'completed').length > 0 && (
                <ItemsTable title="Lab Results" items={(v.lab_orders || []).filter((o) => o.status === 'completed')} visit={v} showResult />
              )}
              <RemovableItems
                title="Prescribed Medications (tick to remove before approving)"
                items={(v.prescriptions || []).filter((p) => p.status === 'pending')}
                visit={v}
                idKey="rx_id"
                selected={removals[v.visit_id] || []}
                onToggle={(id) => toggleRemoval(v.visit_id, id)}
                showInstructions
              />
              <div className="flex flex-wrap gap-3 mt-3">
                <Button onClick={() => approve(v, 'rx')} className="bg-green-600 hover:bg-green-700" data-testid={`approve-rx-${v.visit_id}`}>
                  <Check className="w-4 h-4 mr-1" /> {(removals[v.visit_id] || []).length > 0 ? `Remove ${(removals[v.visit_id] || []).length} & Approve` : 'Approve'}
                </Button>
                <Button variant="outline" onClick={() => rejectRx(v, 'return')} className="border-amber-400 text-amber-700 hover:bg-amber-50" data-testid={`reject-rx-return-${v.visit_id}`}>
                  <Undo2 className="w-4 h-4 mr-1" /> Reject → Return to Doctor
                </Button>
                <Button variant="outline" onClick={() => rejectRx(v, 'drop')} className="border-red-300 text-red-600 hover:bg-red-50" data-testid={`reject-rx-drop-${v.visit_id}`}>
                  <Ban className="w-4 h-4 mr-1" /> Reject → Drop & Bill
                </Button>
              </div>
            </VisitCard>
          ))}
        </div>
        </SubSection>
      </Category>
      )}

      {filter === 'all' && (
        <>
          {/* Records */}
          <Category title="Records" subtitle="Pre-authorization numbers already issued" tone="emerald" collapsible defaultOpen={false} badge={preauths.length} testId="approvals-category-records">
        {preauths.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-4 text-center text-sm text-gray-500" data-testid="preauth-list-empty">
            No pre-authorizations issued yet.
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto" data-testid="preauth-list">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left p-2 font-semibold text-gray-600">Pre-auth #</th>
                  <th className="text-left p-2 font-semibold text-gray-600">Patient</th>
                  <th className="text-left p-2 font-semibold text-gray-600">Hospital</th>
                  <th className="text-left p-2 font-semibold text-gray-600">Approved</th>
                  <th className="text-left p-2 font-semibold text-gray-600">Current Stage</th>
                </tr>
              </thead>
              <tbody>
                {preauths.map((v) => (
                  <tr key={v.visit_id} className="border-t border-gray-100">
                    <td className="p-2 font-mono text-emerald-700 font-semibold">{v.preauth?.number}</td>
                    <td className="p-2 text-gray-800">{v.patient_name}</td>
                    <td className="p-2 text-gray-600">{v.hospital_name}</td>
                    <td className="p-2 text-gray-600">{v.preauth?.approved_at ? new Date(v.preauth.approved_at).toLocaleString() : '—'}</td>
                    <td className="p-2">{(v.status || '').replace(/_/g, ' ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
          </Category>

          {/* Settings */}
          <Category title="Approval Thresholds" subtitle="Amounts (USD) above which a hospital's orders need Acacia office approval — per category, with hospital overrides" tone="gray" collapsible defaultOpen={false} testId="approvals-category-settings">
            <div className="grid lg:grid-cols-2 gap-4">
      <ThresholdSettings
        title="Lab Approval Threshold"
        icon={FlaskConical}
        endpoint="lab-threshold"
        value={labThreshold}
        setValue={setLabThreshold}
        hospitals={hospitals}
        authCfg={authCfg}
        idPrefix="lab"
      />
      <ThresholdSettings
        title="Radiology Approval Threshold"
        icon={ScanLine}
        endpoint="rad-threshold"
        value={radThreshold}
        setValue={setRadThreshold}
        hospitals={hospitals}
        authCfg={authCfg}
        idPrefix="rad"
      />
      <ThresholdSettings
        title="Services Approval Threshold"
        icon={Briefcase}
        endpoint="service-threshold"
        value={serviceThreshold}
        setValue={setServiceThreshold}
        hospitals={hospitals}
        authCfg={authCfg}
        idPrefix="service"
      />
      <ThresholdSettings
        title="Prescription Approval Threshold"
        icon={Pill}
        endpoint="rx-threshold"
        value={rxThreshold}
        setValue={setRxThreshold}
        hospitals={hospitals}
        authCfg={authCfg}
        idPrefix="rx"
      />
            </div>
          </Category>
        </>
      )}
    </div>
  );
}

const TONES = {
  amber: 'border-amber-200 bg-amber-50/60 text-amber-800',
  rose: 'border-rose-200 bg-rose-50/60 text-rose-800',
  sky: 'border-sky-200 bg-sky-50/60 text-sky-800',
  teal: 'border-teal-200 bg-teal-50/60 text-teal-800',
  orange: 'border-orange-200 bg-orange-50/60 text-orange-800',
  emerald: 'border-emerald-200 bg-emerald-50/60 text-emerald-800',
  violet: 'border-violet-200 bg-violet-50/60 text-violet-800',
  indigo: 'border-indigo-200 bg-indigo-50/60 text-indigo-800',
  gray: 'border-gray-200 bg-gray-50 text-gray-700',
};

function Category({ title, subtitle, tone = 'gray', collapsible = false, defaultOpen = true, badge, testId, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className={`rounded-xl border ${TONES[tone].split(' ')[0]} bg-white overflow-hidden`} data-testid={testId}>
      <button type="button" onClick={() => collapsible && setOpen((o) => !o)} className={`w-full flex items-center justify-between gap-2 px-4 py-3 text-left ${TONES[tone]} ${collapsible ? 'cursor-pointer' : 'cursor-default'}`} aria-expanded={open} data-testid={testId ? `${testId}-header` : undefined}>
        <span>
          <span className="text-sm font-bold uppercase tracking-wide">{title}{badge !== undefined ? ` (${badge})` : ''}</span>
          {subtitle && <span className="block text-xs font-normal opacity-80 mt-0.5">{subtitle}</span>}
        </span>
        {collapsible && <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />}
      </button>
      {open && <div className="p-4 space-y-5">{children}</div>}
    </section>
  );
}

function SubSection({ title, count, icon: Icon, tone = 'gray', testId, children }) {
  return (
    <div data-testid={testId}>
      <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${TONES[tone].split(' ')[2]}`} /> {title}
        <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${count > 0 ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-500'}`}>{count}</span>
      </h3>
      {children}
    </div>
  );
}


function ThresholdSettings({ title, icon: Icon, endpoint, value, setValue, hospitals, authCfg, idPrefix }) {
  const [defaultInput, setDefaultInput] = useState('');
  const [overrideHospital, setOverrideHospital] = useState('');
  const [overrideAmount, setOverrideAmount] = useState('');

  useEffect(() => { setDefaultInput(String(value.default_usd)); }, [value.default_usd]);

  const save = async (payload) => {
    try {
      const res = await axios.post(`${API}/workflow/${endpoint}`, payload, authCfg);
      setValue({ default_usd: res.data.default_usd, overrides: res.data.overrides });
      toast.success(`${title} updated`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update threshold');
    }
  };

  return (
    <Card className="border-gray-200 shadow-sm">
      <CardHeader className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200 p-4">
        <CardTitle className="flex items-center gap-2 text-gray-800 text-base">
          <Icon className="w-5 h-5" /> {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 space-y-4">
        <div className="flex items-end gap-3">
          <div className="flex-1">
            <Label className="text-gray-700 font-medium">System Default Threshold (USD)</Label>
            <Input type="number" min="0" value={defaultInput} onChange={(e) => setDefaultInput(e.target.value)} className="mt-1" data-testid={`${idPrefix}-threshold-default-input`} />
          </div>
          <Button onClick={() => save({ default_usd: parseFloat(defaultInput) })} disabled={defaultInput === '' || isNaN(parseFloat(defaultInput))} data-testid={`${idPrefix}-threshold-default-save`}>
            Save Default
          </Button>
        </div>
        <p className="text-xs text-gray-500">
          {idPrefix === 'lab' ? 'Lab orders' : idPrefix === 'rad' ? 'Radiology orders' : idPrefix === 'service' ? 'Services added by a doctor (consultation fees excluded)' : 'Prescriptions'} whose total (converted to USD) exceeds the threshold require Acacia office approval before proceeding.
        </p>

        <div className="border-t border-gray-100 pt-4">
          <Label className="text-gray-700 font-medium">Per-Hospital Overrides</Label>
          <div className="flex flex-col sm:flex-row gap-2 mt-2">
            <Select value={overrideHospital} onValueChange={setOverrideHospital}>
              <SelectTrigger className="sm:w-64" data-testid={`${idPrefix}-threshold-override-hospital`}>
                <SelectValue placeholder="Select hospital" />
              </SelectTrigger>
              <SelectContent>
                {hospitals.filter((h) => h.hospital_name !== 'System Administration').map((h) => (
                  <SelectItem key={h.hospital_name} value={h.hospital_name}>{h.hospital_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input type="number" min="0" placeholder="Threshold (USD)" value={overrideAmount} onChange={(e) => setOverrideAmount(e.target.value)} className="sm:w-40" data-testid={`${idPrefix}-threshold-override-amount`} />
            <Button
              onClick={() => { save({ override: { hospital_name: overrideHospital, threshold_usd: parseFloat(overrideAmount) } }); setOverrideAmount(''); }}
              disabled={!overrideHospital || overrideAmount === '' || isNaN(parseFloat(overrideAmount))}
              data-testid={`${idPrefix}-threshold-override-save`}
            >
              Set Override
            </Button>
          </div>
          {(value.overrides || []).length > 0 && (
            <div className="mt-3 space-y-1">
              {value.overrides.map((o) => (
                <div key={o.hospital_name} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg text-sm" data-testid={`${idPrefix}-threshold-override-row-${o.hospital_name}`}>
                  <span className="text-gray-800">{o.hospital_name}: <strong>${o.threshold_usd} USD</strong></span>
                  <button onClick={() => save({ override: { hospital_name: o.hospital_name, threshold_usd: null } })} className="text-red-500 hover:text-red-700" data-testid={`${idPrefix}-threshold-override-remove-${o.hospital_name}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
